import moment from 'moment';
import _ from 'lodash';
import { mapState, mapGetters } from 'vuex';
import firebase from 'firebase';
import { db } from '@/db';

export default {
  name: 'filters-mixin',

  data() {
    return {
      teams: [],
      players: null,
      trainers: [],
      team_switches: {
        region_filter: false,
        need_filter: false,
        country_filter: true,
      },
      trainer_switches: {
        sex_filter: false,
        type_filter: false,
        license_filter: false,
        age_filter: false,
        language_filter: false,
        region_filter: false,
        country_filter: true,
      },
      player_switches: {
        sex_filter: false,
        language_filter: false,
        position_filter: false,
        foot_filter: false,
        age_filter: false,
        region_filter: false,
        country_filter: true,
      },
      defaultFilterValues: {
        team_switches: {
          region_filter: false,
          need_filter: false,
          country_filter: false,
        },
        trainer_switches: {
          no_team: false,
          sex_filter: false,
          type_filter: false,
          license_filter: false,
          age_filter: false,
          language_filter: false,
          region_filter: false,
          country_filter: false,
        },
        player_switches: {
          no_team: false,
          sex_filter: false,
          language_filter: false,
          position_filter: false,
          foot_filter: false,
          age_filter: false,
          region_filter: false,
          country_filter: false,
        },
      },
      sorted: {
        players: false,
        teams: false,
        trainers: false,
      },
      limits: {
        players: 20,
        teams: 20,
        trainers: 20,
      },
      query: {
        players: null,
        teams: null,
        trainers: null,
      },
      onlineUsers: [],
      loadingSettings: true,
      tab: '',
      freeMatches: 2,
    };
  },

  watch: {
    tab(newVal) {
      this.$store.state.user_data.selectedTab = newVal;
    },
    user_data: {
      handler(value) {
        const userRef = db.collection(this.userType).doc(this.user_data.id);
        userRef.update(value);
      },
      deep: true,
    },

    player_switches: {
      handler(value) {
        if (!this.loadingSettings) {
          const userRef = db.collection('settings').doc(this.user_data.id);
          userRef.set({ player_switches: value }, { merge: true });
        }
      },
      deep: true,
    },
    trainer_switches: {
      handler(value) {
        if (!this.loadingSettings) {
          const userRef = db.collection('settings').doc(this.user_data.id);
          userRef.set({ trainer_switches: value }, { merge: true });
        }
      },
      deep: true,
    },
    team_switches: {
      handler(value) {
        if (!this.loadingSettings) {
          const userRef = db.collection('settings').doc(this.user_data.id);
          userRef.set({ team_switches: value }, { merge: true });
        }
      },
      deep: true,
    },
  },

  computed: {
    ...mapState(['user_data', 'userType', 'nationalitiesObj', 'onlineUsersForFilters']),
    ...mapGetters(['watchlistProfileIds']),

    noLeaguePackage() {
      if (['1', '2', '3'].includes(this.user_data.category.category)) {
        return false;
      }
      return this.teamHasNoActivePackage;
    },

    isTop3LeagueAndInactive() {
      if (
        ['1', '2', '3'].includes(this.user_data.category.category) &&
        this.teamHasNoActivePackage
      ) {
        return true;
      }
      return false;
    },

    teamHasNoActivePackage() {
      const currentDate = moment();

      const subscriptionExpired =
        this.user_data.leaguePackage && this.user_data.leaguePackage.activeToDate
          ? moment([this.user_data.leaguePackage.activeToDate, 5, 30]) < currentDate
          : false;
      const matched = this.user_data.matches ? this.user_data.matches.length : 0;
      const dematched = this.user_data.matches_rejected
        ? this.user_data.matches_rejected.length
        : 0;
      return (
        subscriptionExpired ||
        (!this.user_data.leaguePackage && matched + dematched >= this.freeMatches)
      );
    },

    noSubscription() {
      let subscriptionExpired = false;

      if (this.user_data.subscriptionData) {
        const { isCanceld, subscriptionExpiryDate } = this.user_data.subscriptionData;
        subscriptionExpired = isCanceld ? moment().unix() > subscriptionExpiryDate : false;
      }

      const matched = this.user_data.matches ? this.user_data.matches.length : 0;
      const dematched = this.user_data.matches_rejected
        ? this.user_data.matches_rejected.length
        : 0;

      return (
        subscriptionExpired ||
        (!this.user_data.subscriptionData &&
          matched + dematched >= this.freeMatches)
      );
    },

    // for blurry results
    noSubscriptionFreeMatches() {
      const matched = this.user_data.matches ? this.user_data.matches.length : 0;
      const dematched = this.user_data.matches_rejected
        ? this.user_data.matches_rejected.length
        : 0;
      const noSubscription = !this.user_data.subscriptionData;

      return noSubscription && matched + dematched < this.freeMatches;
    },

    allFilteredPlayers() {
      if (!this.players) {
        return [];
      }

      // reset limit
      this.limits.players = 20;

      let players = [
        ...this.players.sort(
          (a, b) => !!a.accountNotActivated - !!b.accountNotActivated,
        ),
      ];

      if (this.query.players) {
        const query = this.query.players.toLowerCase();
        players = players.filter((r) => r.name && r.name.toLowerCase().includes(query));
      }

      const {
        sex_filter,
        position_filter,
        language_filter,
        age_filter,
        region_filter,
        country_filter,
        foot_filter,
        no_team,
        matched,
        dematched,
        watchlist,
        with_avatar,
        is_online,
        account_active,
      } = this.player_switches;

      if (is_online) {
        players = players.filter((p) => this.onlineUsersForFilters.includes(p.id));
      }

      if (no_team) {
        players = players.filter((p) => p.hasTeam === false || p.hasTeam === 'ja');
      }

      if (account_active) {
        players = players.filter((p) => !p.accountNotActivated);
      }

      if (with_avatar) {
        players = players.filter(
          (p) => p.big_thumbnail && p.big_thumbnail.includes('profilepic'),
        );
      }

      if (matched && dematched) {
        players = players.filter(
          (p) =>
            (this.user_data.already_matched &&
              this.user_data.already_matched.includes(p.id)) ||
            (this.user_data.already_matched_and_rejected &&
              this.user_data.already_matched_and_rejected.includes(p.id)),
        );
      } else {
        if (matched) {
          players = players.filter(
            (p) =>
              this.user_data.already_matched &&
              this.user_data.already_matched.includes(p.id),
          );
        }

        if (dematched) {
          players = players.filter(
            (p) =>
              this.user_data.already_matched_and_rejected &&
              this.user_data.already_matched_and_rejected.includes(p.id) &&
              (!this.user_data.already_matched ||
                (this.user_data.already_matched &&
                  !this.user_data.already_matched.includes(p.id))),
          );
        }
      }

      if (watchlist) {
        players = players.filter((p) => this.watchlistProfileIds.includes(p.id));
      }

      if (
        sex_filter &&
        this.user_data.player_sex &&
        this.user_data.player_sex.length > 0
      ) {
        players = players.filter((player) => {
          if (player.sex) {
            return this.user_data.player_sex.includes(player.sex);
          }
          return false;
        });
      }

      if (country_filter && this.user_data.player_country) {
        players = players.filter(
          (player) =>
            this.user_data.player_country === player.living_in_country ||
            (player.nationality &&
              !player.living_in_country &&
              this.user_data.player_country ===
                this.nationalitiesObj[player.nationality]),
        );
      }

      if (
        position_filter &&
        this.user_data.player_positions &&
        this.user_data.player_positions.length > 0
      ) {
        players = players.filter((player) => {
          if (player.position_specific && player.position_specific.length > 0) {
            return this.user_data.player_positions.includes(player.position_specific);
          }
          return false;
        });
      }

      if (
        foot_filter &&
        this.user_data.player_foot &&
        this.user_data.player_foot.length > 0
      ) {
        players = players.filter((player) => {
          if (player.foot) {
            return this.user_data.player_foot.includes(player.foot);
          }
          return false;
        });
      }

      if (
        language_filter &&
        this.user_data.player_languages &&
        this.user_data.player_languages.length > 0
      ) {
        players = players.filter((player) => {
          if (player.languages && player.languages.length > 0) {
            const intersection = _.intersection(
              this.user_data.player_languages,
              player.languages,
            );
            if (intersection.length >= 1) {
              return true;
            }
            return false;
          }
          return false;
        });
      }

      if (age_filter) {
        if (!this.user_data.player_age_range) {
          this.$set(this.user_data, 'player_age_range', [14, 20]);
        }

        players = players.filter((player) => {
          const allowedplayer_age_range = this.user_data.player_age_range;
          const playerAge = moment().diff(player.age, 'years');
          const minAge = allowedplayer_age_range[0];
          const maxAge = allowedplayer_age_range[1];
          const isInRange = !(playerAge <= minAge || playerAge >= maxAge);
          return isInRange;
        });
      }

      if (
        region_filter &&
        this.user_data.player_category &&
        this.user_data.player_verband &&
        this.user_data.player_verband.length > 0
      ) {
        players = players.filter((player) => {
          if (player.category) {
            // Check 61 71 81 are leagues that have the same category but have different names
            if (
              player.category.category === '61' ||
              player.category.category === '71' ||
              player.category.category === '81'
            ) {
              player.category.category = player.category.category.slice(0, -1);
            }

            if (
              this.user_data.player_category === '61' ||
              this.user_data.player_category === '71' ||
              this.user_data.player_category === '81'
            ) {
              this.user_data.player_category = this.user_data.player_category.slice(
                0,
                -1,
              );
            }
            return (
              this.user_data.player_category.includes(player.category.category) &&
              player.verband === this.user_data.player_verband
            );
          }
          return false;
        });
      }

      return players;
    },

    filteredPlayers() {
      return this.allFilteredPlayers.slice(0, this.limits.players);
    },

    allFilteredTrainers() {
      if (!this.trainers) {
        return [];
      }

      // reset limit
      this.limits.trainers = 20;

      let trainers = [
        ...this.trainers.sort(
          (a, b) => !!a.accountNotActivated - !!b.accountNotActivated,
        ),
      ];

      if (this.query.trainers) {
        const query = this.query.trainers.toLowerCase();
        trainers = trainers.filter(
          (r) => r.name && r.name.toLowerCase().includes(query),
        );
      }

      const {
        sex_filter,
        type_filter,
        license_filter,
        language_filter,
        region_filter,
        country_filter,
        age_filter,
        no_team,
        matched,
        dematched,
        watchlist,
        with_avatar,
        is_online,
        account_active,
      } = this.trainer_switches;

      if (is_online) {
        trainers = trainers.filter((p) => this.onlineUsersForFilters.includes(p.id));
      }

      if (no_team) {
        trainers = trainers.filter((p) => p.hasTeam === false || p.hasTeam === 'ja');
      }

      if (account_active) {
        console.log('accountNotActivated filter');
        trainers = trainers.filter((p) => !p.accountNotActivated);
      }

      if (with_avatar) {
        trainers = trainers.filter(
          (p) => p.big_thumbnail && p.big_thumbnail.includes('profilepic'),
        );
      }

      if (matched && dematched) {
        trainers = trainers.filter(
          (p) =>
            (this.user_data.already_matched &&
              this.user_data.already_matched.includes(p.id)) ||
            (this.user_data.already_matched_and_rejected &&
              this.user_data.already_matched_and_rejected.includes(p.id)),
        );
      } else {
        if (matched) {
          trainers = trainers.filter(
            (p) =>
              this.user_data.already_matched &&
              this.user_data.already_matched.includes(p.id),
          );
        }

        if (dematched) {
          trainers = trainers.filter(
            (p) =>
              this.user_data.already_matched_and_rejected &&
              this.user_data.already_matched_and_rejected.includes(p.id) &&
              (!this.user_data.already_matched ||
                (this.user_data.already_matched &&
                  !this.user_data.already_matched.includes(p.id))),
          );
        }
      }

      if (watchlist) {
        trainers = trainers.filter((p) => this.watchlistProfileIds.includes(p.id));
      }

      if (
        sex_filter &&
        this.user_data.trainer_sex &&
        this.user_data.trainer_sex.length > 0
      ) {
        console.log('trener sdf');
        trainers = trainers.filter((trainer) => {
          if (trainer.sex) {
            return this.user_data.trainer_sex.includes(trainer.sex);
          }
          return false;
        });
      }

      if (country_filter && this.user_data.trainer_country) {
        trainers = trainers.filter(
          (t) =>
            this.user_data.trainer_country === t.living_in_country ||
            (t.nationality &&
              !t.living_in_country &&
              this.user_data.trainer_country === this.nationalitiesObj[t.nationality]),
        );
      }

      if (
        type_filter &&
        this.user_data.trainer_types &&
        this.user_data.trainer_types.length > 0
      ) {
        trainers = trainers.filter((trainer) => {
          if (trainer.trainer_type) {
            return this.user_data.trainer_types.includes(trainer.trainer_type);
          }
          return false;
        });
      }

      if (
        license_filter &&
        this.user_data.trainer_license &&
        this.user_data.trainer_license.length > 0
      ) {
        trainers = trainers.filter((trainer) => {
          console.log(trainer.license);
          if (trainer.license) {
            return this.user_data.trainer_license.includes(trainer.license);
          }
          return false;
        });
      }

      if (
        language_filter &&
        this.user_data.trainer_languages &&
        this.user_data.trainer_languages.length > 0
      ) {
        trainers = trainers.filter((trainer) => {
          if (trainer.languages && trainer.languages.length > 0) {
            const intersection = _.intersection(
              this.user_data.trainer_languages,
              trainer.languages,
            );
            if (intersection.length >= 1) {
              return true;
            }
            return false;
          }
          return false;
        });
      }

      if (age_filter) {
        if (!this.user_data.trainer_age_range) {
          this.$set(this.user_data, 'trainer_age_range', [14, 20]);
        }

        trainers = trainers.filter((trainer) => {
          const allowedtrainer_age_range = this.user_data.trainer_age_range;
          const trainerAge = moment().diff(trainer.age, 'years');
          const minAge = allowedtrainer_age_range[0];
          const maxAge = allowedtrainer_age_range[1];
          const isInRange = !(trainerAge <= minAge || trainerAge >= maxAge);
          return isInRange;
        });
      }

      if (
        region_filter &&
        this.user_data.trainer_category &&
        this.user_data.trainer_verband
      ) {
        trainers = trainers.filter((trainer) => {
          if (trainer.category) {
            return (
              this.user_data.trainer_category.includes(trainer.category.category) &&
              trainer.verband === this.user_data.trainer_verband
            );
          }
          return false;
        });
      }

      // .filter(trainer => trainer.league == this.user_data.trainer_league)
      return trainers;
    },

    filteredTrainers() {
      return this.allFilteredTrainers.slice(0, this.limits.trainers);
    },

    allFilteredTeams() {
      // reset limit
      this.limits.teams = 20;

      let teams = [
        ...this.teams.sort((a, b) => !!a.accountNotActivated - !!b.accountNotActivated),
      ];

      if (this.query.teams) {
        const query = this.query.teams.toLowerCase();
        teams = teams.filter(
          (r) => r.team_name && r.team_name.toLowerCase().includes(query),
        );
      }

      const {
        country_filter,
        need_filter,
        region_filter,
        matched,
        dematched,
        watchlist,
        with_avatar,
        is_online,
        account_active,
      } = this.team_switches;

      if (is_online) {
        teams = teams.filter((p) => this.onlineUsersForFilters.includes(p.id));
      }

      if (account_active) {
        teams = teams.filter((p) => !p.accountNotActivated);
      }

      if (with_avatar) {
        teams = teams.filter(
          (p) => p.big_thumbnail && p.big_thumbnail.includes('profilepic'),
        );
      }

      if (matched && dematched) {
        teams = teams.filter(
          (p) =>
            (this.user_data.already_matched &&
              this.user_data.already_matched.includes(p.id)) ||
            (this.user_data.already_matched_and_rejected &&
              this.user_data.already_matched_and_rejected.includes(p.id)),
        );
      } else {
        if (matched) {
          teams = teams.filter(
            (p) =>
              this.user_data.already_matched &&
              this.user_data.already_matched.includes(p.id),
          );
        }

        if (dematched) {
          teams = teams.filter(
            (p) =>
              this.user_data.already_matched_and_rejected &&
              this.user_data.already_matched_and_rejected.includes(p.id) &&
              (!this.user_data.already_matched ||
                (this.user_data.already_matched &&
                  !this.user_data.already_matched.includes(p.id))),
          );
        }
      }

      if (watchlist) {
        teams = teams.filter((p) => this.watchlistProfileIds.includes(p.id));
      }

      if (region_filter && this.user_data.team_league && this.user_data.team_verband) {
        teams = teams.filter((team) => {
          if (team.category) {
            return (
              this.user_data.team_league.includes(team.category.league) &&
              team.verband === this.user_data.team_verband
            );
          }
          return false;
        });
      }

      if (country_filter && this.user_data.team_country) {
        teams = teams.filter(
          (team) => this.user_data.team_country === team.nationality,
        );
      }

      if (need_filter && this.user_data.team_need) {
        if (
          this.user_data.team_need === 'Spieler' &&
          this.user_data.team_need_positions &&
          this.user_data.team_need_positions.length > 0
        ) {
          teams = teams.filter((team) => {
            const intersection = _.intersection(
              team.playerprofile_position,
              this.user_data.team_need_positions,
            );
            return intersection.length >= 1;
          });
        }

        if (
          this.user_data.team_need === 'Trainer' &&
          this.user_data.team_need_license &&
          this.user_data.team_need_license.length > 0
        ) {
          teams = teams.filter((team) => {
            const intersection = _.intersection(
              team.trainerprofile_type,
              this.user_data.team_need_license,
            );
            return intersection.length >= 1;
          });
        }

        if (
          this.user_data.team_need === 'Spieler & Trainer' &&
          (this.user_data.team_need_license || this.user_data.team_need_positions) &&
          (this.user_data.team_need_license.length > 0 ||
            this.user_data.team_need_positions.length > 0)
        ) {
          teams = teams.filter((team) => {
            const intersection1 = _.intersection(
              team.trainerprofile_type,
              this.user_data.team_need_license,
            );
            const intersection2 = _.intersection(
              team.playerprofile_position,
              this.user_data.team_need_positions,
            );
            return intersection1.length >= 1 || intersection2.length >= 1;
          });
        }
      }

      return teams;
    },

    filteredTeams() {
      return this.allFilteredTeams.slice(0, this.limits.teams);
    },
  },

  methods: {
    filterName: _.debounce(function setQuery(q, type) {
      this.query[type] = q;
    }, 600),

    updateFilters(type, value, userType) {
      this.$set(this[userType], type, value);
      // this[userType][type] = value;
      console.log(type, value, userType);
    },

    resetFilters(type) {
      console.log('reseted', type);
      this[type] = {
        ...this[type],
        ...this.defaultFilterValues[type],
      };
      let selection = {};

      if (type === 'player_switches') {
        selection = {
          player_country: '',
          player_verband: '',
          player_category: [],
          player_sex: [],
          player_positions: [],
          player_foot: [],
          player_languages: [],
          player_age_range: [14, 20],
        };
      }

      if (type === 'team_switches') {
        selection = {
          team_country: '',
          team_verband: '',
          team_need: '',
          team_need_positions: [],
          team_need_license: [],
          team_league: [],
        };
      }
      if (type === 'trainer_switches') {
        selection = {
          trainer_country: '',
          trainer_verband: '',
          trainer_category: [],
          trainer_sex: [],
          trainer_types: [],
          trainer_license: [],
          trainer_languages: [],
          trainer_age_range: [20, 30],
        };
      }
      this.$store.state.user_data = {
        ...this.$store.state.user_data,
        ...selection,
      };
    },

    getUserOnlineStatus() {
      const statuses = firebase.database().ref('user_status');

      statuses.once('value').then((snapshot) => {
        const data = snapshot.val();
        Object.keys(data).forEach((key) => {
          if (data[key].connected) {
            this.onlineUsers.push(key);
          }
        });

        this.$store.commit('updateOnlineUsersForFilters', this.onlineUsers);
      });
    },
  },

  async created() {
    this.loadingSettings = true;
    const savedFilters = await db
      .collection('settings')
      .doc(this.user_data.id)
      .get()
      .then((snapshot) => snapshot.data());

    if (savedFilters && savedFilters.player_switches) {
      this.player_switches = _.omit(savedFilters.player_switches, [
        'watchlist',
        'matched',
        'dematched',
        'is_online',
        'with_avatar',
        'account_active',
      ]);
    }

    if (savedFilters && savedFilters.trainer_switches) {
      this.trainer_switches = _.omit(savedFilters.trainer_switches, [
        'watchlist',
        'matched',
        'dematched',
        'is_online',
        'with_avatar',
        'account_active',
      ]);
    }

    if (savedFilters && savedFilters.team_switches) {
      this.team_switches = _.omit(savedFilters.team_switches, [
        'watchlist',
        'matched',
        'dematched',
        'is_online',
        'with_avatar',
        'account_active',
      ]);
    }
    this.loadingSettings = false;
  },
};
